/**
 * Authentication API - Cookie-based auth
 */
import { apiFetch, apiFetchJSON } from './client';

/**
 * Login user with email and password
 * Returns user data and sets HttpOnly cookie
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{success: boolean, user: object}>}
 */
export async function login(email, password) {
  const response = await apiFetch('/api/family/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  // CRITICAL: Read response text first (can only be read once)
  const responseText = await response.text();
  
  // Parse JSON from text
  let data;
  try {
    data = JSON.parse(responseText);
  } catch (e) {
    data = { detail: 'Failed to parse server response' };
  }
  
  if (!response.ok) {
    throw new Error(data.detail || 'Invalid credentials');
  }

  return data;
}

/**
 * Get current user from session (cookie-based)
 * @returns {Promise<object|null>}
 */
export async function getCurrentUser() {
  try {
    const response = await apiFetch('/debug/me');
    
    if (!response.ok) {
      return null;
    }

    // Read text first to avoid "body already used" error
    const responseText = await response.text();
    const data = JSON.parse(responseText);
    
    // Debug endpoint returns {ok: true, user_id, email, ...}
    if (data.ok) {
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('getCurrentUser failed:', error);
    return null;
  }
}

/**
 * Get full user profile from /api/family/me
 * @returns {Promise<object|null>}
 */
export async function getMe() {
  try {
    console.log('🔐 Fetching user profile from /api/family/me...');
    const response = await apiFetch('/api/family/me');
    
    if (!response.ok) {
      console.warn(`⚠️ /api/family/me returned ${response.status}`);
      
      // 404 = user not found (stale cookie)
      // 401 = unauthorized (no/invalid cookie)
      if (response.status === 404) {
        console.warn('⚠️ User not found (404) - stale session detected');
      } else if (response.status === 401) {
        console.warn('⚠️ Unauthorized (401) - invalid/missing session');
      }
      
      return null;
    }

    // Read text first to avoid "body already used" error
    const responseText = await response.text();
    const user = JSON.parse(responseText);
    console.log('✅ User profile loaded:', user.email);
    return user;
  } catch (error) {
    console.error('❌ getMe failed:', error);
    
    // If it's a network error or parsing error, log it
    if (error.name === 'TypeError') {
      console.error('⚠️ Network error or CORS issue');
    }
    
    return null;
  }
}

/**
 * Logout user (clear cookie)
 * @returns {Promise<void>}
 */
export async function logout() {
  try {
    await apiFetch('/api/family/logout', {
      method: 'POST',
    });
  } catch (error) {
    console.error('Logout failed:', error);
  }
}

/**
 * Complete invitation (Phase 16)
 */
export async function completeInvitation(token, name, password) {
  return apiFetchJSON('/api/family/complete-invite', {
    method: 'POST',
    body: JSON.stringify({ token, name, password }),
  });
}
